import axios from 'axios';
import { ALFRESCO_BASE_URL, ALFRESCO_API_URL, ALFRESCO_SERVICE_URL, ALFRESCO_LEGACY_API_URL, API_ENDPOINTS } from '../utils/constants';
import { getTicket, setTicket, clearTicket, setUserInfo } from '../utils/auth';

// Configuration Axios pour l'API Public (workflow, authentication)
const api = axios.create({
  baseURL: ALFRESCO_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Configuration Axios pour les WebScripts custom (stats, etc.)
const serviceApi = axios.create({
  baseURL: ALFRESCO_SERVICE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Configuration Axios pour l'ancienne API s/api
const legacyApi = axios.create({
  baseURL: ALFRESCO_LEGACY_API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Intercepteur pour ajouter le ticket Alfresco à chaque requête (API Public)
api.interceptors.request.use(
  (config) => {
    const ticket = getTicket();
    if (ticket) {
      config.params = {
        ...config.params,
        alf_ticket: ticket
      };
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour ajouter le ticket Alfresco à chaque requête (Service API)
serviceApi.interceptors.request.use(
  (config) => {
    const ticket = getTicket();
    if (ticket) {
      config.params = {
        ...config.params,
        alf_ticket: ticket
      };
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs d'authentification
const handleAuthError = (error) => {
  if (error.response && error.response.status === 401) {
    // Session expirée, déconnexion
    clearTicket();
    window.location.href = '/login';
  }
  return Promise.reject(error);
};

api.interceptors.response.use(
  (response) => response,
  handleAuthError
);

serviceApi.interceptors.response.use(
  (response) => response,
  handleAuthError
);

legacyApi.interceptors.request.use(
  (config) => {
    const ticket = getTicket();
    if (ticket) {
      config.params = {
        ...config.params,
        alf_ticket: ticket
      };
    }
    return config;
  },
  (error) => Promise.reject(error)
);

legacyApi.interceptors.response.use(
  (response) => response,
  handleAuthError
);

/**
 * Authentification Alfresco
 * @param {string} userId - Nom d'utilisateur
 * @param {string} password - Mot de passe
 * @returns {Promise} - Ticket Alfresco et informations utilisateur
 */
export const login = async (userId, password) => {
  try {
    // Authentification via l'API legacy Alfresco service/api/login
    const response = await axios.post(
      API_ENDPOINTS.LOGIN,
      {
        username: userId,  // L'API legacy utilise "username" pas "userId"
        password
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    // Le ticket est dans data.ticket de la réponse legacy
    const ticket = response.data?.data?.ticket;
    
    if (!ticket) {
      throw new Error('Ticket non reçu de Alfresco');
    }

    // Stocker le ticket
    setTicket(ticket);

    // Récupérer les informations utilisateur avec -me- (utilisateur actuellement authentifié)
    try {
      const userInfoResponse = await api.get(API_ENDPOINTS.PEOPLE_ME);
      const userInfo = userInfoResponse.data?.entry;
      
      // Stocker les informations utilisateur
      if (userInfo) {
        setUserInfo(userInfo);
      }
    } catch (userError) {
      console.warn('Impossible de récupérer les informations utilisateur:', userError);
    }

    return {
      ticket,
      userId
    };
  } catch (error) {
    console.error('Erreur lors de l\'authentification:', error);
    if (error.response?.status === 401) {
      throw new Error('Identifiants incorrects');
    }
    throw error;
  }
};

/**
 * Déconnexion
 */
export const logout = () => {
  clearTicket();
};

/**
 * Récupère les informations de l'utilisateur actuellement connecté
 * @returns {Promise} - Informations utilisateur
 */
export const getCurrentUser = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.PEOPLE_ME);
    const userInfo = response.data?.entry;
    
    if (userInfo) {
      setUserInfo(userInfo);
    }
    
    return userInfo;
  } catch (error) {
    console.error('Erreur lors de la récupération des informations utilisateur:', error);
    throw error;
  }
};

/**
 * Récupère les tâches workflow avec options avancées
 * @param {Object} options - Filtres et pagination
 * @param {string} options.assignee - Filtrer par assigné
 * @param {string} options.state - État (active, completed, suspended...)
 * @param {number} options.skipCount - Nombre d'éléments à ignorer
 * @param {number} options.maxItems - Nombre maximum d'éléments retournés
 * @param {Array<string>} options.properties - Propriétés supplémentaires à inclure
 * @param {Array<string>} options.orderBy - Critères de tri
 * @param {string} options.where - Clause where personnalisée
 */
export const getTasks = async ({
  assignee = null,
  state = 'active',
  skipCount = 0,
  maxItems = 100,
  properties = ['*'],
  orderBy = ['dueDate ASC'],
  where = ''
} = {}) => {
  try {
    const params = {
      skipCount,
      maxItems
    };

    if (state) {
      params.state = state === 'IN_PROGRESS' ? 'active' : state;
    }

    if (Array.isArray(properties) && properties.length > 0) {
      params.properties = properties;
    }

    if (Array.isArray(orderBy) && orderBy.length > 0) {
      params.orderBy = orderBy;
    }

    if (where) {
      params.where = where;
    } else if (assignee) {
      params.where = `(assignee='${assignee}')`;
    }

    const response = await api.get(API_ENDPOINTS.WORKFLOW.TASKS, { params });

    const entries = response.data?.list?.entries || [];

    return entries.map(({ entry }) => ({
      id: entry?.id,
      name: entry?.name,
      description: entry?.description,
      assignee: entry?.assignee,
      owner: entry?.owner,
      state: entry?.state,
      priority: entry?.priority,
      dueDate: entry?.dueDate,
      created: entry?.createdAt,
      startDate: entry?.startDate,
      endDate: entry?.endedAt,
      workflowId: entry?.processId,
      processDefinitionId: entry?.processDefinitionId,
      formKey: entry?.formResourceKey,
      serviceTaskName: entry?.serviceTaskName,
      variables: entry?.variables || {},
      properties: entry?.properties || {}
    }));
  } catch (error) {
    console.error('Erreur lors de la récupération des tâches:', error);
    throw error;
  }
};

/**
 * Récupère les tâches via l'ancienne API s/api/task-instances
 */
export const getLegacyTasks = async ({
  authority = null,
  properties = ['bpm_priority', 'bpm_status', 'bpm_dueDate', 'bpm_description'],
  exclude = 'wcmwf:*',
  skipCount = 0,
  maxItems = 50,
  state = null,
  pooledTasks = null
} = {}) => {
  try {
    const params = {
      skipCount,
      maxItems,
      properties: properties.join(','),
      exclude
    };
    if (authority) {
      params.authority = authority;
    }
    if (state) {
      params.state = state;
    }
    if (pooledTasks !== null) {
      params.pooledTasks = pooledTasks;
    }
    const response = await legacyApi.get('/task-instances', { params });
    return (response.data?.data || []).map((task) => ({
      id: task.id,
      name: task.name,
      title: task.title,
      description: task.description,
      state: task.state,
      assignee: task.owner?.userName,
      owner: task.owner,
      isPooled: task.isPooled,
      isClaimable: task.isClaimable,
      isEditable: task.isEditable,
      priority: task.properties?.bpm_priority,
      dueDate: task.properties?.bpm_dueDate,
      outcome: task.outcome,
      workflowInstance: task.workflowInstance,
      properties: task.properties,
      propertyLabels: task.propertyLabels
    }));
  } catch (error) {
    console.error('Erreur lors de la récupération des tâches (legacy):', error);
    throw error;
  }
};

/**
 * Récupère le détail d'une tâche via l'API legacy
 * @param {string} taskId
 * @param {boolean} detailed - inclure toutes les propriétés et informations de workflow
 */
export const getLegacyTaskDetail = async (taskId, { detailed = true } = {}) => {
  try {
    const params = {
      noCache: Date.now()
    };
    if (detailed) {
      params.detailed = true;
    }
    const response = await legacyApi.get(`/task-instances/${taskId}`, { params });
    return response.data?.data;
  } catch (error) {
    console.error('Erreur lors de la récupération du détail de la tâche (legacy):', error);
    throw error;
  }
};

/**
 * Récupère le formulaire d'une tâche (via WebScript custom BIAT)
 * @param {string} taskId - ID de la tâche
 * @returns {Promise} - Définition du formulaire
 */
export const getTaskForm = async (taskId) => {
  try {
    const response = await serviceApi.get(API_ENDPOINTS.BIAT.TASK_FORM(taskId));
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération du formulaire:', error);
    throw error;
  }
};

/**
 * Complète une tâche avec les données du formulaire (via WebScript custom BIAT)
 * @param {string} taskId - ID de la tâche
 * @param {Object} formData - Données du formulaire
 * @returns {Promise} - Résultat de la complétion
 */
export const completeTask = async (taskId, formData) => {
  try {
    const response = await serviceApi.post(
      API_ENDPOINTS.BIAT.COMPLETE_TASK(taskId),
      formData
    );
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la complétion de la tâche:', error);
    throw error;
  }
};

/**
 * Soumet un formulaire de tâche via l'endpoint formprocessor d'Alfresco
 * @param {string} taskId - ID de la tâche (format: activiti$1234)
 * @param {Object} formData - Données du formulaire avec préfixes prop_ et assoc_
 * @returns {Promise} - Résultat de la soumission
 */
export const submitTaskForm = async (taskId, formData) => {
  try {
    // Encoder le taskId pour l'URL (ex: activiti$1234 -> activiti%241234)
    const encodedTaskId = encodeURIComponent(taskId);
    
    const ticket = getTicket();
    const response = await fetch(
      `${ALFRESCO_LEGACY_API_URL}/task/${encodedTaskId}/formprocessor?alf_ticket=${ticket}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur lors de la soumission: ${response.status} - ${errorText}`);
    }

    // Alfresco peut retourner du HTML ou du JSON selon le cas
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else {
      // Si c'est du HTML ou autre, retourner un objet de succès
      return { success: true };
    }
  } catch (error) {
    console.error('Erreur lors de la soumission du formulaire:', error);
    throw error;
  }
};

/**
 * Récupère les variables d'un workflow (via WebScript custom BIAT)
 * @param {string} processId - ID du processus workflow
 * @returns {Promise} - Variables du workflow
 */
export const getWorkflowVariables = async (processId) => {
  try {
    const response = await serviceApi.get(API_ENDPOINTS.BIAT.WORKFLOW_VARIABLES(processId));
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des variables:', error);
    throw error;
  }
};

/**
 * Récupère les statistiques BIAT (via WebScripts custom)
 * @param {string} endpoint - Endpoint des statistiques (global, by-status, by-agent, workflow-history)
 * @param {Object} params - Paramètres additionnels (optionnel)
 * @returns {Promise} - Statistiques
 */
export const getStats = async (endpoint, params = {}) => {
  try {
    const endpointMap = {
      'global': API_ENDPOINTS.BIAT.STATS.GLOBAL,
      'by-status': API_ENDPOINTS.BIAT.STATS.BY_STATUS,
      'by-agent': API_ENDPOINTS.BIAT.STATS.BY_AGENT,
      'workflow-history': API_ENDPOINTS.BIAT.STATS.WORKFLOW_HISTORY
    };
    
    const endpointPath = endpointMap[endpoint];
    if (!endpointPath) {
      throw new Error(`Endpoint de statistiques inconnu: ${endpoint}`);
    }

    const response = await serviceApi.get(endpointPath, { params });
    return response.data;
  } catch (error) {
    console.error(`Erreur lors de la récupération des statistiques (${endpoint}):`, error);
    throw error;
  }
};

/**
 * Récupère les nœuds (fichiers et dossiers) d'un dossier
 * @param {string} nodeId - ID du nœud parent (par défaut: '-root-')
 * @param {Object} options - Options de requête
 * @returns {Promise} - Liste des nœuds
 */
export const getNodes = async (nodeId = '-root-', options = {}) => {
  try {
    const params = {
      skipCount: options.skipCount || 0,
      maxItems: options.maxItems || 100,
      include: options.include || ['properties', 'path']
    };

    // Utiliser l'URL complète avec l'instance axios configurée
    const url = `${ALFRESCO_API_URL}/alfresco/versions/1/nodes/${nodeId}/children`;
    const response = await axios.get(url, {
      params: {
        ...params,
        alf_ticket: getTicket()
      }
    });

    return (response.data?.list?.entries || []).map(({ entry }) => entry);
  } catch (error) {
    console.error('Erreur lors de la récupération des nœuds:', error);
    throw error;
  }
};

/**
 * Recherche des nœuds par terme
 * @param {string} term - Terme de recherche
 * @param {Object} options - Options de recherche
 * @returns {Promise} - Résultats de recherche
 */
export const searchNodes = async (term, options = {}) => {
  try {
    const params = {
      term,
      skipCount: options.skipCount || 0,
      maxItems: options.maxItems || 100,
      include: options.include || ['properties', 'path']
    };

    // Utiliser l'URL complète avec l'instance axios configurée
    const url = `${ALFRESCO_API_URL}/alfresco/versions/1/queries/nodes`;
    const response = await axios.get(url, {
      params: {
        ...params,
        alf_ticket: getTicket()
      }
    });

    return (response.data?.list?.entries || []).map(({ entry }) => entry);
  } catch (error) {
    console.error('Erreur lors de la recherche:', error);
    throw error;
  }
};

/**
 * Télécharge un document
 * @param {string} nodeId - ID du nœud à télécharger
 * @param {string} filename - Nom du fichier
 * @returns {Promise} - Blob du fichier
 */
export const downloadNode = async (nodeId, filename) => {
  try {
    const ticket = getTicket();
    const url = `${ALFRESCO_API_URL}/alfresco/versions/1/nodes/${nodeId}/content?attachment=true&alf_ticket=${ticket}`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Erreur lors du téléchargement');
    }

    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('Erreur lors du téléchargement du fichier:', error);
    throw error;
  }
};

/**
 * Récupère les détails d'un nœud spécifique
 * @param {string} nodeId - ID du nœud
 * @returns {Promise} - Détails du nœud
 */
export const getNodeDetails = async (nodeId) => {
  try {
    const url = `${ALFRESCO_API_URL}/alfresco/versions/1/nodes/${nodeId}`;
    const response = await axios.get(url, {
      params: {
        include: ['properties', 'path', 'permissions'],
        alf_ticket: getTicket()
      }
    });

    return response.data?.entry;
  } catch (error) {
    console.error('Erreur lors de la récupération des détails du nœud:', error);
    throw error;
  }
};

export default {
  login,
  logout,
  getCurrentUser,
  getTasks,
  getLegacyTasks,
  getLegacyTaskDetail,
  getTaskForm,
  completeTask,
  submitTaskForm,
  getWorkflowVariables,
  getStats,
  getNodes,
  searchNodes,
  downloadNode,
  getNodeDetails
};

